import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { AlarmClock, Ban, Building2, CheckCircle2, CircleDot, ExternalLink, FileText, Hand, HelpCircle, Link2, MailX, Maximize2, MessageSquareText, Pause, Play, Printer, RotateCcw, Shuffle, Siren, Sparkles, TriangleAlert, Wrench, X, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ToneBadge } from '@/components/composites/status-badge'
import { UserAvatar } from '@/components/composites/user-avatar'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { usePermission } from '@/features/permissions/usePermission'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import {
  formatDuration,
  lateness,
  MILESTONE_LABEL,
  RELATION_LABEL,
  STATE_LABEL,
  statusTone,
  useConversation,
  useDeskAction,
  useDeskActivity,
  useDeskCase,
  useDeskLinks,
  useDeskRelated,
  type MilestoneView,
  type SupportCase,
} from './api'
import { ActivityFeed, RESOLUTION_LABEL, when } from './Activity'
import { ClientSideDialog, DismissDialog, NotResolvedDialog, OutcomeDialog, EscalateDialog, FixedDialog, ReassignDialog, RelatedDialog, SeverityDialog, TextActionDialog, TriageDialog } from './Dialogs'
import { Countdown, SeverityBadge, useClients, useMembersById, useNow } from './parts'
import { printTicketRecord } from './printRecord'
import { ContextPanel, DraftPanel, holdNote } from './QuickPaths'
import { ThemeLine } from './Insights'

const STATUS_WORD: Record<MilestoneView['status'], string> = {
  met: 'Met',
  missed: 'Missed',
  on_track: 'On track',
  at_risk: 'At risk',
  breached: 'Breached',
  paused: 'Paused',
}

function Milestones({ c, now }: { c: SupportCase; now: number }) {
  return (
    <ol className="grid gap-1.5">
      {c.clock.milestones.map((m) => {
        const left = m.deadlineAt ? Date.parse(m.deadlineAt) - now : null
        return (
          <li key={m.milestone} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border-soft px-3 py-2 text-sm">
            <span className="w-36 font-medium">{MILESTONE_LABEL[m.milestone]}</span>
            <ToneBadge tone={statusTone(m.status)} size="xs">{STATUS_WORD[m.status]}</ToneBadge>
            <span className="text-xs text-text-secondary">
              {m.doneAt ? `Done ${when(m.doneAt)}` : m.deadlineAt ? `Due ${when(m.deadlineAt)}` : 'Waiting on the customer'}
            </span>
            {!m.doneAt && left !== null ? (
              <span className={cn('num ml-auto text-xs font-semibold', left < 0 ? 'text-danger' : 'text-text-secondary')}>
                {lateness(left)}
              </span>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

/** What the customer reported and who they are. Their messages are on the activity feed. */
function TicketDetails({ c }: { c: SupportCase }) {
  const conv = useConversation(c.sourceId, c.sourceKind !== 'manual')
  const d = conv.data
  const fields = Object.entries(d?.attributes ?? {}).filter(([, v]) => v.trim())
  return (
    <section className="grid gap-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold"><FileText className="size-4" /> Ticket details</h3>
      <div className="grid gap-3 rounded-lg border border-border-soft p-3 text-sm">
        {d ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-secondary">
            {d.ticketType ? <span><strong>Type:</strong> {d.ticketType}</span> : null}
            {d.category ? <span><strong>Category:</strong> {d.category}</span> : null}
            {d.stateLabel ? <span><strong>Intercom status:</strong> {d.stateLabel}</span> : null}
            {d.createdAt ? <span><strong>Created:</strong> {when(d.createdAt)}</span> : null}
          </div>
        ) : null}
        <div className="grid gap-1">
          <p className="text-xs font-semibold">What the customer reported</p>
          <p className="whitespace-pre-wrap text-text-secondary">{d?.description?.trim() || c.summary || 'No description on the ticket.'}</p>
        </div>
        {c.sourceKind === 'manual' ? (
          <p className="text-xs text-text-muted">Reported by {c.requester ?? 'unknown'} via {c.channel} · first reported {when(c.firstContactAt)}</p>
        ) : conv.isPending ? (
          <p className="text-xs text-text-muted">Reading the ticket from Intercom…</p>
        ) : !d ? (
          <p className="text-xs text-text-muted">Intercom could not be reached right now; this is the copy Tapwork kept.</p>
        ) : null}
        {d?.contacts?.length ? (
          <div className="grid gap-1">
            <p className="text-xs font-semibold">Customer</p>
            <ul className="grid gap-0.5 text-text-secondary">
              {d.contacts.map((ct) => <li key={ct.id}>{[ct.name, ct.email, ct.companyName].filter(Boolean).join(' · ') || 'Unnamed contact'}</li>)}
            </ul>
          </div>
        ) : null}
        {fields.length ? (
          <dl className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-3 gap-y-1 text-xs">
            {fields.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-medium text-text-secondary">{k.replace(/_/g, ' ')}</dt>
                <dd className="whitespace-pre-wrap break-words text-foreground">{v}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </section>
  )
}

/** The client on the ticket, and changing it — remembered for the requester's future tickets when asked. */
function ClientPanel({ c, mayAct }: { c: SupportCase; mayAct: boolean }) {
  const clients = useClients()
  const links = useDeskLinks()
  const [editing, setEditing] = useState(false)
  const [pick, setPick] = useState(c.clientId ?? '')
  const [remember, setRemember] = useState(c.channel === 'intercom')
  return (
    <section className="grid gap-2 rounded-lg border border-border-soft p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Client</h3>
        {mayAct && !editing ? <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Change client</Button> : null}
      </div>
      {!editing ? (
        <p className="text-sm">
          {c.clientName ?? <span className="text-text-muted">Not matched</span>}
          {c.requester ? <span className="block text-xs text-text-secondary">Reported by {c.requester}</span> : null}
        </p>
      ) : (
        <div className="grid gap-2">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger aria-label="Client"><SelectValue placeholder="Pick the client" /></SelectTrigger>
            <SelectContent>
              {clients.filter((cl) => !cl.archived).map((cl) => <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {c.channel === 'intercom' ? (
            <label className="flex items-start gap-2 text-xs text-text-secondary">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(v === true)} className="mt-0.5" />
              <span>Remember this for {c.requester ?? 'this requester'}'s future tickets</span>
            </label>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setPick(c.clientId ?? '') }}>Cancel</Button>
            <Button
              size="sm"
              disabled={!pick || pick === c.clientId || links.isPending}
              onClick={() => links.mutate({ id: c.id, body: { clientId: pick, rememberRequester: c.channel === 'intercom' && remember } }, { onSuccess: () => setEditing(false) })}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

function RelatedPanel({ c, mayAct, onOpen }: { c: SupportCase; mayAct: boolean; onOpen: (id: string) => void }) {
  const related = useDeskRelated(c.id, Boolean(c.related?.length))
  const run = useDeskAction()
  const [adding, setAdding] = useState(false)
  const items = c.related?.length ? (related.data?.items ?? []) : []
  return (
    <section className="grid gap-2 rounded-lg border border-border-soft p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Related tickets</h3>
        {mayAct ? <Button size="sm" variant="ghost" onClick={() => setAdding(true)}><Link2 className="size-4" /> Link a ticket</Button> : null}
      </div>
      {!items.length ? (
        <p className="text-xs text-text-muted">{c.related?.length && related.isPending ? 'Loading…' : 'None. Link duplicates, the same issue reported twice, or tickets from one incident.'}</p>
      ) : (
        <ul className="grid gap-1.5">
          {items.map((r) => (
            <li key={r.caseId} className="flex items-start gap-2 text-sm">
              <ToneBadge tone={r.kind === 'part_of_incident' || r.kind === 'incident' ? 'danger' : 'neutral'} size="xs">{RELATION_LABEL[r.kind]}</ToneBadge>
              <button type="button" className="min-w-0 flex-1 text-left hover:underline" onClick={() => onOpen(r.caseId)}>
                <span className="num mr-1 text-tiny text-text-muted">{r.reference}</span>
                {r.title}
                <span className="block text-tiny text-text-muted">{r.clientName ?? 'Client not matched'} · {STATE_LABEL[r.state]}</span>
              </button>
              {mayAct ? (
                <Button size="icon" variant="ghost" className="size-7" aria-label={`Unlink ${r.reference}`} disabled={run.isPending} onClick={() => run.mutate({ id: c.id, action: 'related/remove', body: { otherId: r.caseId } })}>
                  <X className="size-3.5" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {adding ? <RelatedDialog c={c} open onOpenChange={(v) => !v && setAdding(false)} /> : null}
    </section>
  )
}

function LinksPanel({ c, mayAct }: { c: SupportCase; mayAct: boolean }) {
  const links = useDeskLinks()
  const [jira, setJira] = useState<string | null>(null)
  return (
    <section className="grid gap-2 rounded-lg border border-border-soft p-3">
      <h3 className="text-sm font-semibold">Links</h3>
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid gap-1">
          <Label htmlFor="sd-jira" className="text-xs">Jira CSR</Label>
          <Input id="sd-jira" className="h-8 w-40" value={jira ?? c.jiraKey ?? ''} onChange={(e) => setJira(e.target.value)} placeholder="CSR-123" disabled={!mayAct} />
        </div>
        <Button size="sm" variant="outline" disabled={!mayAct || jira === null || links.isPending} onClick={() => links.mutate({ id: c.id, body: { jiraKey: jira?.trim() || null } }, { onSuccess: () => setJira(null) })}>Save</Button>
        {c.githubUrl ? (
          <a className="inline-flex items-center gap-1 text-sm text-brand underline" href={c.githubUrl} target="_blank" rel="noreferrer">GitHub issue <ExternalLink className="size-3" /></a>
        ) : c.ownerTeam ? (
          <span className="text-xs text-text-muted">No GitHub issue yet.</span>
        ) : null}
      </div>
    </section>
  )
}

function ResolutionPanel({ c }: { c: SupportCase }) {
  const feed = useDeskActivity(c.id)
  const latest = [...(feed.data?.items ?? [])].reverse().find((i) => i.kind === 'resolution' && i.resolution)
  if (!latest?.resolution && !c.resolutionNote) return null
  return (
    <section className="grid gap-2 rounded-lg border border-success/30 bg-success-soft p-3">
      <h3 className="text-sm font-semibold">Resolution</h3>
      {c.resolutionNote ? <p className="text-sm">{c.resolutionNote}{c.rootCause ? <span className="text-text-secondary"> · {c.rootCause}</span> : null}</p> : null}
      {latest?.resolution ? (
        <dl className="grid gap-1.5 text-sm">
          {(['problem', 'fix', 'cause', 'prevention'] as const).filter((k) => latest.resolution![k]?.trim()).map((k) => (
            <div key={k}>
              <dt className="text-xs font-semibold">{RESOLUTION_LABEL[k]}</dt>
              <dd className="whitespace-pre-wrap text-text-secondary">{latest.resolution![k]}</dd>
            </div>
          ))}
          <p className="text-tiny text-text-muted">Approved by {latest.authorName} · {when(latest.at)}</p>
        </dl>
      ) : null}
    </section>
  )
}

type DialogKind = 'blocked' | 'triage' | 'severity' | 'ask' | 'restored' | 'fixed' | 'client-side' | 'outcome' | 'not-resolved' | 'resolve' | 'reassign' | 'close' | 'reopen' | 'dismiss' | 'escalate' | 'update' | 'no-response' | null

/** The commitments on top of the milestones: the 30-minute update, a silent customer, a recovery ETA. */
function Commitments({ c, now }: { c: SupportCase; now: number }) {
  const rows: Array<{ key: string; label: string; detail: string; tone: 'danger' | 'warning' | 'neutral' }> = []
  if (c.update) {
    const left = Date.parse(c.update.dueAt) - now
    rows.push({
      key: 'update',
      label: 'Next customer update',
      detail: left < 0 ? `${formatDuration(left)} overdue · last ${when(c.update.lastAt)}` : `due in ${formatDuration(left)} · last ${when(c.update.lastAt)}`,
      tone: left < 0 ? 'danger' : left <= 5 * 60_000 ? 'warning' : 'neutral',
    })
  }
  if (c.waitingFrom) {
    const hours = Math.floor((now - Date.parse(c.waitingFrom)) / 3_600_000)
    rows.push({
      key: 'waiting',
      label: c.state === 'fixed' ? 'Waiting for confirmation' : 'Waiting on the customer',
      detail: `${hours}h since ${when(c.waitingFrom)}${hours >= 72 ? ' · review and close' : hours >= 24 ? ' · follow up' : ''}`,
      tone: hours >= 72 ? 'danger' : hours >= 24 ? 'warning' : 'neutral',
    })
  }
  const hold = holdNote(c)
  if (hold) rows.push({ key: 'hold', label: hold.label, detail: hold.detail, tone: hold.tone })
  if (c.eta) {
    const missed = Date.parse(c.eta.at) < now && ['triage', 'helpdesk', 'engineering'].includes(c.state)
    rows.push({ key: 'eta', label: 'Recovery ETA', detail: `${when(c.eta.at)}${missed ? ' · missed' : ''} · ${c.eta.byName}: ${c.eta.reason}`, tone: missed ? 'danger' : 'neutral' })
  }
  if (!rows.length) return null
  return (
    <ul className="grid gap-1.5">
      {rows.map((r) => (
        <li key={r.key} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md border border-border-soft px-3 py-2 text-sm">
          <span className="w-44 font-medium">{r.label}</span>
          <span className={cn('text-xs', r.tone === 'danger' ? 'text-danger' : r.tone === 'warning' ? 'text-warning' : 'text-text-secondary')}>{r.detail}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * The whole ticket: header, actions and milestones, then two columns — the facts on the left
 * (details, client, related, links, resolution) and the activity feed on the right — stacking on
 * narrow screens. Shown in the slide-in from the queue and as its own page.
 */
export function CaseScreen({ c, mode, onOpenCase }: { c: SupportCase; mode: 'sheet' | 'page'; onOpenCase: (id: string) => void }) {
  const { workspaceSlug = '' } = useParams()
  const now = useNow()
  const members = useMembersById()
  const { can } = usePermission()
  const me = useIdentity().userId
  const run = useDeskAction()
  const feed = useDeskActivity(c.id)
  const related = useDeskRelated(c.id, Boolean(c.related?.length))
  const [dialog, setDialog] = useState<DialogKind>(null)
  const mayAct = can('update', 'Card')
  const assignee = c.assigneeId ? members.map.get(c.assigneeId) : undefined
  const open = !['closed', 'dismissed'].includes(c.state)

  return (
    <div className="grid gap-5">
      <header className="grid gap-2 text-left">
        <div className="flex flex-wrap items-center gap-2">
          {c.state === 'dismissed' && !c.severity ? null : <SeverityBadge severity={c.severity} size="md" className="text-xs font-semibold" />}
          <ToneBadge tone="neutral" size="md" className="text-xs font-semibold text-foreground">
            {c.state === 'dismissed' ? dismissedLabel(c) : STATE_LABEL[c.state]}
          </ToneBadge>
          {c.waiting ? <ToneBadge tone="info" size="md" className="text-xs font-semibold"><Pause className="mr-1 size-3.5" />Waiting on customer</ToneBadge> : null}
          <span className="num text-sm font-medium text-muted-foreground">#{c.reference}</span>
          <span className="ml-auto flex gap-1 pr-8">
            {mode === 'sheet' ? (
              <Button asChild size="sm" variant="ghost">
                <Link to={paths.supportDeskTicket(workspaceSlug, c.id)}><Maximize2 className="size-4" /> Open full page</Link>
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => printTicketRecord(c, feed.data?.items ?? [], related.data?.items ?? [], (id) => members.map.get(id)?.displayName ?? null)}
            >
              <Printer className="size-4" /> Print
            </Button>
          </span>
        </div>
        <h2 className="text-lg font-semibold leading-snug">{c.title}</h2>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary">
          <span>{c.clientName ?? 'Client not matched'}</span>
          {c.requester ? <span>{c.requester}</span> : null}
          <span>{c.ownerTeam ?? 'Helpdesk'}</span>
          {assignee ? <span className="inline-flex items-center gap-1"><UserAvatar person={assignee} size="xs" />{assignee.displayName}</span> : <span>Unassigned</span>}
          <span>First contact {when(c.firstContactAt)} · {c.channel === 'intercom' ? 'Intercom' : c.channel}</span>
        </div>
        <ThemeLine c={c} mayAct={mayAct} />
      </header>

      <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
        <Countdown c={c} now={now} />
        <span className="text-xs text-text-muted">Customer waiting {formatDuration(c.ageMs)} on the SLA clock</span>
      </section>

      {mayAct && open ? (
        <section className="flex flex-wrap gap-2">
          {c.state === 'triage' && c.suggestedSeverity && c.suggestedTeam && c.suggestionReason !== 'No AI suggestion available' ? (
            <Button size="sm" disabled={run.isPending} title={`Accept the suggestion as it stands${c.suggestedTeam !== 'Helpdesk' ? ': the team lead is named and the handoff is drafted from the conversation' : ''}`} onClick={() => run.mutate({ id: c.id, action: 'accept-suggestion' })}>
              <Sparkles className="size-4" /> Accept: {c.suggestedSeverity} · {c.suggestedTeam}
            </Button>
          ) : null}
          {c.state === 'triage' ? <Button size="sm" variant={c.suggestedSeverity ? 'outline' : 'default'} onClick={() => setDialog('triage')}><CircleDot className="size-4" /> Triage</Button> : null}
          {c.state === 'helpdesk' ? <Button size="sm" onClick={() => setDialog('triage')}><Shuffle className="size-4" /> Send to a team</Button> : null}
          {(c.state === 'engineering' || c.state === 'helpdesk' || c.state === 'triage') && c.assigneeId !== me ? (
            <Button size="sm" variant={c.state === 'engineering' ? 'default' : 'outline'} disabled={run.isPending} onClick={() => run.mutate({ id: c.id, action: 'pickup' })}><Hand className="size-4" /> I'm on it</Button>
          ) : null}
          {c.state === 'engineering' ? <Button size="sm" variant="outline" onClick={() => setDialog('restored')}><Play className="size-4" /> Restored</Button> : null}
          {c.state === 'engineering' || c.state === 'restored' ? <Button size="sm" variant="outline" onClick={() => setDialog('fixed')}><Wrench className="size-4" /> Fixed</Button> : null}
          {(c.state === 'engineering' || c.state === 'restored') && c.ownerTeam ? (
            <Button size="sm" variant="outline" title="The cause is in the client's own setup: hand it back to helpdesk" onClick={() => setDialog('client-side')}><Building2 className="size-4" /> Client-side cause</Button>
          ) : null}
          {c.state === 'helpdesk' || c.state === 'triage' ? <Button size="sm" variant="outline" onClick={() => setDialog('resolve')}><CheckCircle2 className="size-4" /> Resolve</Button> : null}
          {c.state === 'fixed' ? <Button size="sm" onClick={() => setDialog('close')}><CheckCircle2 className="size-4" /> Customer confirmed — close</Button> : null}
          {c.state === 'fixed' ? <Button size="sm" variant="outline" title="The customer says it is still broken, or needs something else" onClick={() => setDialog('not-resolved')}><RotateCcw className="size-4" /> Not resolved</Button> : null}
          {c.waiting ? (
            <Button size="sm" variant="outline" disabled={run.isPending} onClick={() => run.mutate({ id: c.id, action: 'customer-replied' })}><Play className="size-4" /> Customer replied</Button>
          ) : c.state !== 'fixed' ? (
            <Button size="sm" variant="outline" onClick={() => setDialog('ask')}><HelpCircle className="size-4" /> Ask customer</Button>
          ) : null}
          {c.update ? <Button size="sm" variant="outline" onClick={() => setDialog('update')}><MessageSquareText className="size-4" /> Log update</Button> : null}
          {c.waitingFrom ? <Button size="sm" variant="outline" onClick={() => setDialog('no-response')}><MailX className="size-4" /> Close — no response</Button> : null}
          {c.state !== 'fixed' ? (
            <Button size="sm" variant="ghost" disabled={run.isPending} title="Hold the Slack reminders for 2 hours; one reminder then if it is still overdue" onClick={() => run.mutate({ id: c.id, action: 'snooze', body: { hours: 2 } })}>
              <AlarmClock className="size-4" /> Remind me in 2h
            </Button>
          ) : null}
          {c.state !== 'fixed' ? <Button size="sm" variant="ghost" onClick={() => setDialog('blocked')}><Ban className="size-4" /> Blocked…</Button> : null}
          <Button size="sm" variant="outline" className="text-danger" onClick={() => setDialog('escalate')}><Siren className="size-4" /> Escalate</Button>
          <Button size="sm" variant="ghost" onClick={() => setDialog('severity')}><TriangleAlert className="size-4" /> Priority</Button>
          <Button size="sm" variant="ghost" onClick={() => setDialog('reassign')}><Shuffle className="size-4" /> Reassign</Button>
          {c.state === 'triage' || c.state === 'helpdesk' ? <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDialog('dismiss')}><XCircle className="size-4" /> Not an issue</Button> : null}
          {c.state === 'triage' ? (
            <Button size="sm" variant="ghost" disabled={run.isPending} onClick={() => run.mutate({ id: c.id, action: 'suggest' })}><Sparkles className="size-4" /> Suggest</Button>
          ) : null}
        </section>
      ) : mayAct ? (
        <div className="grid gap-2">
          {c.outcomeNeeded ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/35 bg-warning-soft p-3">
              <p className="text-sm">
                <span className="font-semibold">Closed in Intercom while {c.ownerTeam ?? 'engineering'} had it.</span>{' '}
                Record how it ended so the reports are right.
              </p>
              <Button size="sm" onClick={() => setDialog('outcome')}><CheckCircle2 className="size-4" /> Record outcome</Button>
            </div>
          ) : null}
          <Button size="sm" variant="outline" className="justify-self-start" onClick={() => setDialog('reopen')}><RotateCcw className="size-4" /> Reopen</Button>
        </div>
      ) : null}

      <DraftPanel c={c} mayAct={mayAct && open} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <div className="grid content-start gap-5">
          <section className="grid gap-2">
            <h3 className="text-sm font-semibold">SLA milestones</h3>
            <Milestones c={c} now={now} />
            <Commitments c={c} now={now} />
          </section>
          <ResolutionPanel c={c} />
          <TicketDetails c={c} />
          {open ? <ContextPanel c={c} onOpenCase={onOpenCase} /> : null}
          <ClientPanel key={`${c.id}:${c.clientId}`} c={c} mayAct={mayAct} />
          <RelatedPanel c={c} mayAct={mayAct} onOpen={onOpenCase} />
          <LinksPanel c={c} mayAct={mayAct} />
        </div>
        <ActivityFeed c={c} mayAct={mayAct} />
      </div>

      {dialog === 'triage' ? <TriageDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} /> : null}
      {dialog === 'severity' ? <SeverityDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} /> : null}
      {dialog === 'reassign' ? <ReassignDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} /> : null}
      {dialog === 'dismiss' ? <DismissDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} /> : null}
      {dialog === 'not-resolved' ? <NotResolvedDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} onOpenCase={onOpenCase} /> : null}
      {dialog === 'outcome' ? <OutcomeDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} /> : null}
      {dialog === 'client-side' ? <ClientSideDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} /> : null}
      {dialog === 'fixed' || dialog === 'resolve' ? <FixedDialog c={c} helpdesk={dialog === 'resolve'} open onOpenChange={(v) => !v && setDialog(null)} /> : null}
      {dialog === 'ask' ? (
        <TextActionDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} action="ask" field="question" title="Ask the customer" description="With auto-replies on, the question is sent in Intercom and the clock pauses until they reply. With them off, send it in Intercom yourself: the clock pauses once your message is there, not before." label="Question" placeholder="Which candidate email is affected, and when did they last try?" submitLabel="Record the question" />
      ) : null}
      {dialog === 'restored' ? (
        <TextActionDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} action="restored" field="note" title="Restored" description="The customer can work again (even if through a workaround). The fix clock keeps running." label="What was done" placeholder="Rolled back the scoring service; interviews load again" submitLabel="Mark restored" />
      ) : null}
      {dialog === 'close' ? (
        <TextActionDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} action="close" field="note" title="Close" description="Close once the customer has confirmed it is working." label="Note (optional)" placeholder="Customer confirmed by chat" submitLabel="Close ticket" />
      ) : null}
      {dialog === 'blocked' ? (
        <TextActionDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} action="blocked" field="reason" title="Blocked" description="Tells the team lead once, in the ticket's Slack thread, and holds the reminders for 4 hours while it is unblocked." label="What is it blocked on?" placeholder="Waiting for the client to send the candidate email" submitLabel="Tell the lead" />
      ) : null}
      {dialog === 'escalate' ? <EscalateDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} /> : null}
      {dialog === 'update' ? (
        <TextActionDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} action="update" field="text" title="Log a customer update" description="Record an update you gave the customer outside Tapwork (Viber, a call, email). Replies in Intercom are picked up by themselves. The next update follows the priority: 30 minutes for P1, 2 hours for P2, a day for P3." label="What you told them (optional)" placeholder="Shared that engineering found the cause; next update by 3:30 PM" submitLabel="Log update" />
      ) : null}
      {dialog === 'no-response' ? (
        <TextActionDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} action="close-no-response" field="note" title="Close — no customer response" description="Only after reviewing the conversation: make sure nothing the customer said was missed or misread. A later reply reopens it." label="What you checked" placeholder="Reviewed the thread; last message was our fix confirmation request, no open questions" submitLabel="Close ticket" />
      ) : null}
      {dialog === 'reopen' ? (
        <TextActionDialog c={c} open onOpenChange={(v) => !v && setDialog(null)} action="reopen" field="reason" title="Reopen" description="Reopens on the same clock history." label="Why" placeholder="Customer says it happened again" submitLabel="Reopen" />
      ) : null}
    </div>
  )
}

/** "Dismissed · Greeting only, customer never came back": the reason from the latest dismissal. */
function dismissedLabel(c: SupportCase): string {
  const reason = [...c.events].reverse().find((e) => e.kind === 'dismissed')?.text
  return reason ? `${STATE_LABEL.dismissed} · ${reason}` : STATE_LABEL.dismissed
}

/** The slide-in from the queue: three quarters of the page, and a link to the full page. */
export function CaseSheet({ id, onClose, onOpenCase }: { id: string | null; onClose: () => void; onOpenCase: (id: string) => void }) {
  const query = useDeskCase(id)
  const c = query.data
  return (
    <Sheet open={Boolean(id)} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:w-3/4 sm:max-w-none">
        {query.isPending ? (
          <LoadingState label="Opening the ticket…" />
        ) : query.error || !c ? (
          <ErrorState error={query.error} onRetry={query.refetch} />
        ) : (
          <>
            <SheetHeader className="sr-only"><SheetTitle>{c.title}</SheetTitle><SheetDescription>Support ticket {c.reference}</SheetDescription></SheetHeader>
            <CaseScreen c={c} mode="sheet" onOpenCase={onOpenCase} />
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
